import { Divider } from '@material-ui/core'
import { useUser } from 'components/providers/UserProvider'
import { useState } from 'react'
import { Permission, PTInventoryArticle } from 'shared'
import { ArticlesSearch } from './ArticlesSearch'
import './backoffice.css'
import { ChangelogManager } from './ChangelogManager'
import { Drawer } from './Drawer'
import { GradingScaleManager } from './GradingScaleManager'
import { InventoryTransactionsSearch } from './InventoryTransactionsSearch'
import { MkmProductsSearch } from './MkmProductsSearch'
import { OrderTransactionsSearch } from './OrderTransactionsSearch'
import { ProductCatalogUpdate } from './ProductCatalogUpdate'
import { StaticPagesManager } from './StaticPagesManager'
import { UserImpersonation } from './UserImpersonation'
import { UserSearch } from './UsersSearch'

export type BackofficeTransactionsTabs =
  | 'inventoryTransactions'
  | 'impersonation'
  | 'orders'
  | 'changelog'
  | 'staticPages'
  | 'productCatalog'

export const getFormattedDate = (d: Date | undefined) => {
  if (d) {
    const localeDate = new Date(d).toLocaleString()
    const [date, time] = localeDate.split(', ')
    return (
      <>
        {date} - {time}
      </>
    )
  } else return <></>
}

const USER_SEARCH_TABS: BackofficeTransactionsTabs[] = [
  'inventoryTransactions',
  'impersonation',
  'orders',
]

export const listRowsPerPage = [25, 50, 100, 150]

export type SortDirection = 'desc' | 'asc'
export const sortDirectionOptions: { value: SortDirection; label: string }[] = [
  { value: 'desc', label: 'Newest first' },
  { value: 'asc', label: 'Oldest first' },
]

export const BackofficePresenter = (): JSX.Element => {
  const { hasPermission } = useUser()

  const tabs: any[] = [
    {
      tab: 'inventoryTransactions',
      transKey: 'InventoryTransactions',
      dataTestid: 'inventory-tab',
      icon: 'fa-light fa-warehouse',
      permission: 'view_inventory_transactions',
    },
    {
      tab: 'impersonation',
      transKey: 'Impersonation',
      dataTestid: 'impersonation-tab',
      icon: 'fa-light fa-user-group',
      permission: 'view_inventory_transactions',
    },
    {
      tab: 'orders',
      transKey: 'Orders',
      dataTestid: 'orders-tab',
      icon: 'fa-light fa-wallet',
      permission: 'view_order_transactions',
    },
    {
      tab: 'productCatalog',
      transKey: 'Product Catalog',
      dataTestid: 'product-catalog-tab',
      icon: 'fa-light fa-database',
      // The whole tab is for manage_database. view_mkm_products opens it too, but only for the
      // MKM products search.
      permission: ['manage_database', 'view_mkm_products'],
    },
    {
      tab: 'changelog',
      transKey: 'Changelog',
      dataTestid: 'changelog-tab',
      icon: 'fa-light fa-megaphone',
      permission: 'manage_changelog',
    },
    {
      tab: 'staticPages',
      transKey: 'Static Pages',
      dataTestid: 'static-pages-tab',
      icon: 'fa-light fa-file-lines',
      permission: 'manage_static_pages',
    },
  ].filter((tab) => {
    const permissions = (
      Array.isArray(tab.permission) ? tab.permission : [tab.permission]
    ) as Permission[]
    return permissions.some((permission) => hasPermission(permission))
  })

  const [checkedUserId, setCheckedUserId] = useState<string | undefined>(undefined)
  const [checkedArticle, setCheckedArticle] = useState<PTInventoryArticle | undefined>(undefined)
  const [openSidebarDrawer, setOpenSidebarDrawer] = useState(false)
  const [activeSidebarTab, setActiveSidebarTab] = useState<BackofficeTransactionsTabs>(
    tabs[0]?.tab ?? 'inventoryTransactions'
  )

  const handleOpenSidebarDrawer = () => {
    setOpenSidebarDrawer(true)
  }

  const handleDrawerClose = () => {
    setOpenSidebarDrawer(false)
  }

  // The article belongs to the previous user: keeping it would load that user's product transactions
  const selectUser = (userId: string | undefined): void => {
    setCheckedUserId(userId)
    setCheckedArticle(undefined)
  }

  const changeSidebarTab = (tab: BackofficeTransactionsTabs): void => {
    setActiveSidebarTab(tab)
  }

  const hasTab = (tab: BackofficeTransactionsTabs): boolean => tabs.some((t) => t.tab === tab)

  const getContentAccordingActiveTab = (activeSidebarTab: BackofficeTransactionsTabs) => {
    if (activeSidebarTab === 'impersonation')
      return checkedUserId && <UserImpersonation userId={checkedUserId} />
    else if (activeSidebarTab === 'changelog') return <ChangelogManager />
    else if (activeSidebarTab === 'staticPages') return <StaticPagesManager />
    // This tab holds the updates to our own database, so the product catalog tool belongs here too.
    else if (activeSidebarTab === 'productCatalog')
      return (
        <>
          {hasPermission('manage_database') && (
            <>
              <GradingScaleManager />
              <div className="my-5">
                <Divider />
              </div>
              <ProductCatalogUpdate />
              <div className="my-5">
                <Divider />
              </div>
            </>
          )}
          {hasPermission('view_mkm_products') && <MkmProductsSearch />}
        </>
      )
  }

  return (
    <>
      <div>
        <div className="d-flex justify-content-between">
          <div
            className={`backoffice-transactions-drawer-root ${
              openSidebarDrawer ? 'open' : 'closed'
            }`}
          >
            <Drawer
              open={openSidebarDrawer}
              onTabChange={changeSidebarTab}
              tabs={tabs}
              handleOpenSidebarDrawer={handleOpenSidebarDrawer}
              handleDrawerClose={handleDrawerClose}
            />
          </div>
          <div
            className={`admin-presenter-content-container ${openSidebarDrawer ? 'open' : 'closed'}`}
          >
            <div className="admin-dashboard-content my-3">
              {/* Same spot in all these tabs, so the search and the chosen user survive a tab switch */}
              {USER_SEARCH_TABS.includes(activeSidebarTab) && (
                <UserSearch checkedUserId={checkedUserId} setCheckedUserId={selectUser} />
              )}
              {/* The search tabs stay mounted while hidden, so their searches and results survive a tab switch */}
              <div className={activeSidebarTab === 'inventoryTransactions' ? '' : 'd-none'}>
                {checkedUserId && hasTab('inventoryTransactions') && (
                  <ArticlesSearch
                    userId={checkedUserId}
                    checkedArticle={checkedArticle}
                    setCheckedArticle={setCheckedArticle}
                  />
                )}
                {checkedUserId && checkedArticle && hasTab('inventoryTransactions') && (
                  <InventoryTransactionsSearch
                    userId={checkedUserId}
                    selectedPtProduct={Number(checkedArticle.ptProduct._id)}
                  />
                )}
              </div>
              <div className={activeSidebarTab === 'orders' ? '' : 'd-none'}>
                {hasTab('orders') && <OrderTransactionsSearch checkedUserId={checkedUserId} />}
              </div>
              {getContentAccordingActiveTab(activeSidebarTab)}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
